import React from 'react';
import { Clock3 } from 'lucide-react';
import { rsvpLabel } from '../../lib/eventOutreachActions';
import { statusMeta } from '../../lib/eventAttendeeStatus';
import { humanize } from '../../lib/historyDisplay';
import { dueState, formatDue } from '../../lib/taskDue';
import { personLabel } from '../../lib/teammateLabels';

/**
 * The compact Event Outreach pieces of Contact Detail (#889): the right-rail card (`ContactEventsRail`, which
 * names this file), the count strip above it (`ContactCountStrip`) and the chips, links and follow-up text the
 * Events tab shares. Every value is the server's row from `GET /api/contacts/{id}/events` (see
 * `hooks/useContactEvents`); nothing here counts or decides state.
 */

export const RAIL_LIMIT = 5;
// The reverse read answers these for a contact the viewer cannot see or an events role they lack: nothing to retry.
const UNAVAILABLE_STATUSES = [403, 404];

/** True when the events read says "not for you" (hidden contact, no events access) rather than "failed". */
export const eventsUnavailable = (events) => UNAVAILABLE_STATUSES.includes(events?.error?.status);

/** "…" while the first page loads, "—" when the read failed, "500+" when the server capped the total. */
export function eventsCountLabel(events) {
    if (events.error) return '—';
    if (events.loading && events.rows.length === 0) return '…';
    return events.truncated ? `${events.total}+` : String(events.total);
}

/** Event Detail's Guests tab opened on this contact (#1013: it was the Outreach board); without events access, just the event. */
export function eventDetailHref(row, contactId, canOpenEvent) {
    const eventId = encodeURIComponent(row.event.event_id);
    return canOpenEvent ? `/events/${eventId}?tab=guests&contact=${encodeURIComponent(contactId)}` : `/events/${eventId}`;
}

/** The Outreach Studio for this contact and event: post-event drafts only once the contact attended. */
export function studioHref(row, contactId) {
    const mode = row.attendance_status === 'attended' ? 'post' : 'pre';
    return `/contacts/${encodeURIComponent(contactId)}/event-outreach?event_id=${encodeURIComponent(row.event.event_id)}&mode=${mode}`;
}

/** An in-app link: a plain click navigates through `navigate`, a modified click keeps the browser's behaviour. */
export function followLink(event, href, navigate) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(href);
}

/** The row's event timing, falling back to the event's own date for a reply without `timing`. */
export const timingOf = (row) => row.timing || {
    local_date: row.event?.date, start_time: row.event?.start_time, timezone: row.event?.timezone,
};

const CHIP = 'inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[10px] font-medium';

/**
 * The guest's pipeline column, RSVP and attendance as three separate chips, never one merged status. The column is
 * the server's `journey_label` (#1013: one derived column); a one-release-old API sends only the outreach stage.
 */
export function EventChips({ row }) {
    const stage = row.journey_label || row.outreach_stage_label || (row.outreach_stage ? humanize(row.outreach_stage) : 'No stage');
    const rsvp = statusMeta(row.rsvp_status);
    const marked = row.attendance_status === 'attended' || row.attendance_status === 'no_show';
    const attendance = marked ? statusMeta(row.attendance_status) : null;
    return (
        <div className="flex flex-wrap gap-1" data-testid="event-chips">
            <span className={`${CHIP} border-violet-200 bg-violet-50 text-violet-700`} title="Guest stage" data-testid="event-stage-chip">{stage}</span>
            <span className={`${CHIP} ${rsvp.border} ${rsvp.bg} ${rsvp.text}`} title="RSVP" data-testid="event-rsvp-chip">{rsvpLabel(row.rsvp_status)}</span>
            {attendance
                ? <span className={`${CHIP} ${attendance.border} ${attendance.bg} ${attendance.text}`} title="Attendance" data-testid="event-attendance-chip">{attendance.label}</span>
                : <span className={`${CHIP} border-dashed border-stone-300 bg-white text-stone-500`} title="Attendance" data-testid="event-attendance-chip">Attendance not marked</span>}
        </div>
    );
}

const FOLLOW_UP_TONES = {
    overdue: 'border-red-200 bg-red-50 text-red-700',
    scheduled: 'border-amber-200 bg-amber-50 text-amber-700',
};

// A date-only follow-up (`date_only`) is a calendar day, not an instant: `due_at` is the end of its UTC day and
// `due_day` its date. Only the date part is shown, never a zone-shifted day, and the "Today" / "Overdue" it reads
// as is the viewer's day (`dueState`, the rule the task strip uses), not the server's UTC end-of-day `state`: west
// of UTC the server calls a task overdue hours before the viewer's day ends, so the same task would read Overdue
// here and Due today on the strip (#889, A-M1). A timed follow-up keeps the server's `state`.
const followUpDate = (followUp, zone) => formatDue(followUp?.date_only && followUp.due_day ? followUp.due_day : followUp?.due_at, zone);
const followUpState = (followUp, zone) => {
    if (!followUp?.date_only) return followUp?.state;
    return dueState(followUp.due_day || followUp.due_at, zone) === 'overdue' ? 'overdue' : 'scheduled';
};
const hasFollowUp = (followUp) => Boolean(followUp) && followUp.state !== 'none' && Boolean(followUp.due_at);

/**
 * The row's next follow-up (the server's rule, shared with the board and My Outreach). `detailed` is the
 * Events tab's sentence: "due Oct 24, Assigned to Dana · created by Sam"; the rail shows a coloured chip.
 */
export function NextFollowUp({ followUp, zone, detailed = false }) {
    if (!hasFollowUp(followUp)) {
        return <span className={detailed ? 'text-stone-500' : 'text-[10px] text-stone-400'} data-testid="event-next-follow-up" data-state="none">No follow-up</span>;
    }
    const state = followUpState(followUp, zone);
    const overdue = state === 'overdue';
    const tone = overdue ? FOLLOW_UP_TONES.overdue : FOLLOW_UP_TONES.scheduled;
    const when = followUpDate(followUp, zone) || 'date to be confirmed';
    if (!detailed) {
        return (
            <span className={`${CHIP} gap-1 ${tone}`} data-testid="event-next-follow-up" data-state={state}>
                <Clock3 className="h-3 w-3" />{overdue ? `Overdue ${when}` : `Follow-up ${when}`}
            </span>
        );
    }
    const assignee = personLabel(followUp.assigned_to_id, followUp.assigned_to_name);
    return (
        <span className={overdue ? 'font-medium text-red-700' : 'text-amber-700'} data-testid="event-next-follow-up" data-state={state}>
            {`due ${when}`}{assignee ? `, Assigned to ${assignee}` : ''}{followUp.created_by_name ? ` · created by ${followUp.created_by_name}` : ''}
        </span>
    );
}

/**
 * The count strip at the top of the right rail (D6): "Deals N · Lead: <stage> · Events N", each part a link to
 * its card or tab. The three values arrive as display strings built from reads the page already makes.
 */
export function ContactCountStrip({ deals, lead, events, onDeals, onLead, onEvents }) {
    const part = 'font-semibold text-stone-800 hover:underline';
    return (
        <nav className="flex flex-wrap items-center gap-x-1 gap-y-1 px-4 py-3 text-xs border-b border-stone-100 text-stone-500" aria-label="Contact summary" data-testid="contact-count-strip">
            <button type="button" onClick={onDeals} className={part} data-testid="count-strip-deals">Deals <span data-testid="count-strip-deals-value">{deals}</span></button>
            <span aria-hidden="true"> · </span>
            <button type="button" onClick={onLead} className={part} data-testid="count-strip-lead">Lead: <span data-testid="count-strip-lead-value">{lead}</span></button>
            <span aria-hidden="true"> · </span>
            <button type="button" onClick={onEvents} className={part} data-testid="count-strip-events">Events <span data-testid="count-strip-events-value">{events}</span></button>
        </nav>
    );
}

function RailRow({ row, contactId, canOpenEvent, zone, onNavigate }) {
    const href = eventDetailHref(row, contactId, canOpenEvent);
    return (
        <a href={href} onClick={(event) => followLink(event, href, onNavigate)} data-testid="contact-events-rail-row" data-event-id={row.event.event_id}
            className="block p-2.5 space-y-1.5 rounded-lg border border-stone-100 hover:border-stone-300 hover:bg-stone-100/30 transition-colors">
            <p className="text-sm font-medium truncate text-stone-800">{row.event.name || 'Untitled event'}</p>
            <EventChips row={row} />
            <NextFollowUp followUp={row.next_follow_up} zone={zone} />
        </a>
    );
}

// While rows are shown after a failed refresh the count reads "—" (the strip's rule for a failed read), which
// must not become "View all — events": the label drops the number until a read succeeds again.
function viewAllLabel(events) {
    if (events.error) return 'View all events';
    const count = eventsCountLabel(events);
    return `View all ${count} event${count === '1' ? '' : 's'}`;
}

/**
 * The right-rail "Event Outreach" card: this contact's first RAIL_LIMIT events, each linking to its Event
 * Detail, and "View all N events" for the Events tab. A hidden contact (404) or a viewer without events
 * access (403) gets no card at all.
 */
export function ContactEventsRail({ events, contactId, canOpenEvent, zone, onNavigate, onViewAll }) {
    const { rows, loading, error, reload } = events;
    if (eventsUnavailable(events)) return null;
    let body;
    if (error && rows.length === 0) {
        body = (
            <div className="py-3 text-center" data-testid="contact-events-rail-error">
                <p className="text-xs text-stone-500">{error.message}</p>
                <button type="button" onClick={reload} className="mt-1 text-xs font-medium text-stone-700 underline" data-testid="contact-events-rail-retry">Retry</button>
            </div>
        );
    } else if (loading && rows.length === 0) {
        body = (
            <div className="space-y-2 animate-pulse" data-testid="contact-events-rail-skeleton" aria-busy="true">
                <div className="h-14 rounded-lg bg-stone-100" />
                <div className="h-14 rounded-lg bg-stone-100" />
            </div>
        );
    } else if (rows.length === 0) {
        body = <p className="py-3 text-xs text-center text-stone-400" data-testid="contact-events-rail-empty">Not on any event yet</p>;
    } else {
        body = (
            <>
                <div className="space-y-2">
                    {rows.slice(0, RAIL_LIMIT).map((row) => (
                        <RailRow key={row.attendee_id || row.event.event_id} row={row} contactId={contactId} canOpenEvent={canOpenEvent} zone={zone} onNavigate={onNavigate} />
                    ))}
                </div>
                {error && (
                    <p className="mt-2 text-[11px] text-stone-500">
                        Could not refresh. <button type="button" onClick={reload} className="font-medium underline" data-testid="contact-events-rail-retry">Retry</button>
                    </p>
                )}
                <button type="button" onClick={onViewAll} className="mt-2 text-xs font-medium text-stone-700 hover:underline" data-testid="contact-events-rail-view-all">
                    {viewAllLabel(events)}
                </button>
            </>
        );
    }
    return (
        <div className="p-4 border-t border-stone-100" data-testid="contact-events-rail">
            <h4 className="mb-3 text-xs font-semibold tracking-wider uppercase text-stone-400">Event Outreach</h4>
            {body}
        </div>
    );
}
